/**
 * HtmlChipEditor.tsx — rich HTML editor with a chip palette, for Calculator
 * Studio's presentation + proposalSection surfaces.
 *
 * Same technique as ProposalBuilderPage's WysiwygEditor: an isolated
 * contenteditable <iframe> so the section renders with the real proposal CSS,
 * with {{token}} placeholders shown as non-editable chips. Chips round-trip to
 * {{token}} on save. The palette is generated from the LIVE config (see chips.ts)
 * so it grows as the admin adds inputs/outputs/slots.
 *
 * No `primary`-named Tailwind classes; inline styles + CSS variables only.
 */

import { useEffect, useRef, useState } from 'react';
import { buildChipPalette, chipLabel, FRAGMENT_TOKENS, type Chip } from './chips';
import type { CalcSpec, StudioInputField } from '../../../api/calculatorBuilder';

const TOKEN_CHIP_CSS = `
  .cs-token-chip{display:inline-block;padding:1px 9px;margin:0 1px;border-radius:6px;
    background:#EEF2FF;color:#3730A3;border:1px solid #C7D2FE;font-size:.82em;font-weight:700;
    line-height:1.5;white-space:nowrap;vertical-align:baseline;cursor:default;user-select:all;
    font-family:Manrope,system-ui,sans-serif;}
  .cs-token-chip[data-fragment="1"]{display:block;text-align:center;padding:10px;margin:6px 0;
    background:#F5F3FF;color:#5B21B6;border:1px dashed #C4B5FD;font-size:.78em;border-radius:9px;}`;

/** {{token}} → non-editable chip span (editor display). */
function htmlToEditor(html: string, palette: Chip[]): string {
  return (html || '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, p: string) => {
    const frag = FRAGMENT_TOKENS.has(p) ? ' data-fragment="1"' : '';
    const label = chipLabel(p, palette);
    return `<span class="cs-token-chip" data-token="${p}"${frag} contenteditable="false">${label}</span>`;
  });
}

/** chip span → {{token}} (for saving). Tolerant of attribute reordering. */
function editorToHtml(html: string): string {
  if (!html) return '';
  return html.replace(
    /<span\b[^>]*\bdata-token="([\w.]+)"[^>]*>[\s\S]*?<\/span>/gi,
    (_m, t: string) => `{{${t}}}`,
  );
}

interface Props {
  html: string;
  spec: CalcSpec;
  inputs?: StudioInputField[];
  css?: string;
  tall?: boolean;
  onChange: (html: string) => void;
}

export default function HtmlChipEditor({ html, spec, inputs = [], css, tall, onChange }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [mode, setMode] = useState<'visual' | 'html'>('visual');
  const palette = buildChipPalette(spec, inputs);

  // Build the isolated editor doc once per css change; html synced separately.
  useEffect(() => {
    if (mode !== 'visual') return;
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(
      '<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>' +
        (css || '') +
        '\nhtml,body{background:#fff;} body{margin:0;padding:14px 16px;min-height:120px;box-sizing:border-box;font-family:Manrope,system-ui,sans-serif;} body:focus{outline:none;} img{max-width:100%;}' +
        TOKEN_CHIP_CSS +
        '</style></head><body contenteditable="true" spellcheck="false">' +
        htmlToEditor(html, palette) +
        '</body></html>',
    );
    doc.close();
    const body = doc.body;
    const handler = () => onChange(editorToHtml(body.innerHTML));
    body.addEventListener('input', handler);
    return () => body.removeEventListener('input', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [css, mode]);

  // Sync external html into the iframe without stomping an in-progress edit.
  useEffect(() => {
    if (mode !== 'visual') return;
    const doc = iframeRef.current?.contentDocument;
    if (doc && doc.body && editorToHtml(doc.body.innerHTML) !== (html || '')) {
      doc.body.innerHTML = htmlToEditor(html, palette);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, mode]);

  const insertChip = (chip: Chip) => {
    if (mode === 'html') {
      onChange(`${html || ''}{{${chip.token}}}`);
      return;
    }
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.body.focus();
    const frag = chip.fragment ? ' data-fragment="1"' : '';
    doc.execCommand(
      'insertHTML',
      false,
      `<span class="cs-token-chip" data-token="${chip.token}"${frag} contenteditable="false">${chip.label}</span>&nbsp;`,
    );
    onChange(editorToHtml(doc.body.innerHTML));
  };

  const exec = (cmd: string, val?: string) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.body.focus();
    doc.execCommand(cmd, false, val);
    onChange(editorToHtml(doc.body.innerHTML));
  };

  // Group chips for the palette dropdowns
  const groups = Array.from(new Set(palette.map((c) => c.group)));

  const btn: React.CSSProperties = {
    height: 26, padding: '0 8px', border: '1px solid var(--line, #e5e7eb)', borderRadius: 6,
    background: 'var(--card, #fff)', cursor: 'pointer', fontSize: 12, color: 'var(--ink-1, #1f2937)',
  };

  return (
    <div style={{ border: '1px solid var(--line, #e5e7eb)', borderRadius: 9, overflow: 'hidden', background: 'var(--card, #fff)' }}>
      {/* toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderBottom: '1px solid var(--line, #e5e7eb)', background: 'var(--bg-1, #f8f9fa)', flexWrap: 'wrap' }}>
        <button type="button" style={btn} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>B</button>
        <button type="button" style={{ ...btn, fontStyle: 'italic' }} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>i</button>
        <select style={btn} onMouseDown={(e) => e.stopPropagation()} defaultValue="p" onChange={(e) => exec('formatBlock', e.target.value)}>
          <option value="p">Paragraph</option>
          <option value="h2">Heading</option>
          <option value="h3">Subheading</option>
        </select>
        <span style={{ width: 1, height: 18, background: 'var(--line, #e5e7eb)' }} />
        {/* chip palette dropdowns */}
        {groups.map((g) => (
          <select
            key={g}
            style={btn}
            value=""
            onChange={(e) => {
              const chip = palette.find((c) => c.token === e.target.value);
              if (chip) insertChip(chip);
              e.target.value = '';
            }}
          >
            <option value="">+ {g}</option>
            {palette.filter((c) => c.group === g).map((c) => (
              <option key={c.token} value={c.token}>{c.label}</option>
            ))}
          </select>
        ))}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          style={{ ...btn, background: mode === 'html' ? 'var(--accent-weak, #eef2ff)' : 'var(--card, #fff)' }}
          onClick={() => setMode((m) => (m === 'visual' ? 'html' : 'visual'))}
        >
          {mode === 'visual' ? '</> HTML' : '👁 Visual'}
        </button>
      </div>

      {mode === 'visual' ? (
        <iframe ref={iframeRef} title="html-editor" style={{ width: '100%', height: tall ? 420 : 240, border: 'none', display: 'block' }} />
      ) : (
        <textarea
          value={html}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          style={{
            width: '100%', height: tall ? 420 : 240, border: 'none', padding: 12, boxSizing: 'border-box',
            fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12.5, resize: 'vertical',
            color: 'var(--ink-1, #1f2937)', background: 'var(--card, #fff)',
          }}
        />
      )}
    </div>
  );
}
